import * as React from "react";
import { User } from "@nextui-org/user";
import { Link } from "@nextui-org/link";
import { CircularProgress } from "@nextui-org/progress";
import { button as buttonStyles } from "@nextui-org/theme";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
} from "@nextui-org/table";

import sectionImage from "../../public/assets/images/legal.png";
import useApi from "../hooks/useApi";

import { ErrorContainer } from "@/components/error-container";
import { title } from "@/components/primitives";
import { CurrencyProps } from "@/interfaces";

export default function CurrenciesPage() {
  const columns: {
    name: string;
    uid: string;
    align: "start" | "center" | "end";
  }[] = [
    { name: "Currency", uid: "currency", align: "start" },
    { name: "Symbol", uid: "code", align: "center" },
    { name: "Estimated Arrival Time", uid: "arrival", align: "end" },
  ];

  const { data, error, loaded } = useApi(
    "/api/external/get-currencies",
    "GET",
    {},
    true,
  );

  const renderCell = React.useCallback((currency: any, columnKey: any) => {
    const cellValue = currency[columnKey];

    switch (columnKey) {
      case "currency":
        return (
          <User
            avatarProps={{
              radius: "lg",
              src: `${import.meta.env.VITE_APP_BACKEND_URL}` + currency.flag,
            }}
            description={`Code: ${currency.code}`}
            name={currency.name}
          >
            {currency.name}
          </User>
        );
      case "code":
        return (
          <div className="flex flex-col">
            <p className="text-bold text-sm capitalize">{currency.symbol}</p>
          </div>
        );
      case "arrival":
        return (
          <div className="flex flex-col">
            <p className="text-bold text-sm capitalize">4hrs</p>
          </div>
        );
      default:
        return cellValue;
    }
  }, []);

  if (!loaded) {
    return (
      <div className="flex flex-col justify-center items-center">
        <div>
          <CircularProgress label="Loading..." />
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorContainer />;
  } else {
    return (
      <div className="flex flex-col justify-center items-center">
        <div className="lg:w-5/6 xl:w-5/6 w-full sm:w-all w-full">
          <div
            className="ct-bg-white min-h-80 py-3 lg:px-12 xl:px-12 px-2 rounded-md"
            data-aos="fade-up"
          >
            <div className="flex flex-row gap-4 justify-center items-center">
              <div className="block w-5/6">
                <h4 className={title({ fullWidth: true })}>
                  Supported Currencies
                </h4>
                <p className="my-5 pt-4 text-md lg:text-lg text-default-600 font-medium block max-w-full">
                  We are making efforts on daily basis to ensure that we provide
                  support for more currencies as part of our commitment to
                  ensure cross-border payments and transfers. <br />
                  Feel free to request support for your currency if not found on
                  this list.
                </p>
              </div>
              <div className="w-1/2 hidden xl:block lg:block">
                <div className="flex flex-col items-end justify-center">
                  <img
                    alt=""
                    className="w-1/2 self-end"
                    src={sectionImage}
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-4 lg:block xl:bblock justify-center items-center mb-8 pb-10 clear-both">
              <Link
                className={buttonStyles({
                  color: "primary",
                  radius: "md",
                  size: "lg",
                  variant: "shadow",
                })}
                href="/contact-us"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>

        <div className="container lg:px-16 xl:px-16 md:px-16 px-2 pb-28">
          <div className="my-8 xl:mx-5 lg:mx-5 sm:mx-3 flex flex-col items-center justify-center">
            <Table aria-label="Currencies">
              <TableHeader columns={columns}>
                {(column: {
                  name: string;
                  uid: string;
                  align: "start" | "center" | "end";
                }) => (
                  <TableColumn key={column.uid} align={`${column.align}`}>
                    {column.name}
                  </TableColumn>
                )}
              </TableHeader>
              <TableBody
                emptyContent={
                  "No currency is listed at the moment. Please try again later!"
                }
                items={data.response ?? []}
              >
                {(item: CurrencyProps) => (
                  <TableRow key={item.code}>
                    {(columnKey) => (
                      <TableCell>{renderCell(item, columnKey)}</TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  }
}
